/**
 * Shared types, mirroring the API schemas in docs/07-api-design.md.
 *
 * These are hand-written rather than generated so the offline layer can add
 * the client-only fields (sync state, local timestamps) that never travel to
 * the server.
 */

export type Role =
  | "volunteer"
  | "team_lead"
  | "data_manager"
  | "microbiologist"
  | "administrator";

/** Roles that see every team's data (docs/02 permission matrix). */
export const ELEVATED_ROLES: Role[] = [
  "data_manager",
  "microbiologist",
  "administrator",
];

export const EXPORT_ROLES = ELEVATED_ROLES;
export const REFERENCE_ROLES: Role[] = ["microbiologist", "administrator"];

export type AgeUnit = "years" | "months" | "days";
export type Sex = "M" | "F" | "unknown";
export type CultureResult =
  | "growth"
  | "no_growth"
  | "no_significant_growth"
  | "mixed";

/** Culture results that forbid an organism or any AST value (E-09, Q-04). */
export const NO_GROWTH_RESULTS: CultureResult[] = [
  "no_growth",
  "no_significant_growth",
];

export type IsolateNumber = "1st" | "2nd" | "3rd";
export type IsolateStatus =
  | "draft"
  | "complete"
  | "needs_review"
  | "illegible"
  | "void";
export type Severity = "blocking" | "warning" | "info";
/** null means "not tested" - blank is a real answer (E-08). */
export type AstValue = "S" | "I" | "R" | null;

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  team_id: string | null;
  is_active: boolean;
  last_login_at: string | null;
  totp_enabled: boolean;
  /** Staff mobile, for notifications. Null until they opt in (NT-06). */
  phone_e164?: string | null;
}

export interface BulkUserItem {
  user: User;
  invite_token: string;
  invite_expires_at: string;
  team_name?: string | null;
}

export interface BulkUserError {
  row: number;
  email?: string | null;
  error: string;
}

export interface BulkUserImportResult {
  created: BulkUserItem[];
  errors: BulkUserError[];
  total_created: number;
  total_failed: number;
}

export interface Team {
  id: string;
  name: string;
  slug: string;
  record_id_prefix: string;
}

export interface QAFlag {
  id: string;
  isolate_id: string;
  code: string;
  severity: Severity;
  message: string;
  field: string | null;
  detected_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_reason: string | null;
}

/** The editable body of an isolate, shared by create and edit. */
export interface IsolatePayload {
  lab_number: string | null;
  hospital_number: string | null;
  age_value: number | null;
  age_unit: AgeUnit | null;
  sex: Sex | null;
  ward_raw: string | null;
  ward_code: string | null;
  specimen_date: string | null;
  specimen_type: string | null;
  culture_result: CultureResult | null;
  organism_code: string | null;
  organism_as_written: string | null;
  isolate_number: IsolateNumber | null;
  ast_results: Record<string, AstValue>;
  status: IsolateStatus;
  register_book: string | null;
  register_page: string | null;
  notes: string | null;
}

export interface Isolate extends IsolatePayload {
  id: string;
  client_uuid: string;
  record_id: string;
  team_id: string;
  author_id: string;
  author_name: string | null;
  reference_version: number;
  entered_offline: boolean;
  synced_at: string | null;
  created_at: string;
  updated_at: string;
  qa_flags: QAFlag[];
  can_edit?: boolean;
}

export interface IsolatePage {
  items: Isolate[];
  next_cursor: string | null;
  total: number | null;
}

// --- reference data --------------------------------------------------------
export interface Organism {
  code: string;
  name: string;
  gram: "negative" | "positive" | "other";
  whonet_code: string | null;
  is_active: boolean;
}

export interface Antibiotic {
  code: string;
  name: string;
  drug_class: string | null;
  /** Awaiting microbiologist sign-off (docs/STATUS.md open items). */
  is_tbc: boolean;
  sort_order: number;
  is_active: boolean;
}

export interface Ward {
  code: string;
  name: string;
  department: string | null;
  location_type: "inx" | "icu" | "out" | "eme" | "other";
  is_active: boolean;
}

export interface Specimen {
  code: string;
  name: string;
  whonet_code: string | null;
  is_urine: boolean;
  is_inpatient_typical: boolean;
  is_active: boolean;
}

export interface QARuleConfig {
  code: string;
  description: string;
  severity: Severity;
  definition: Record<string, unknown>;
  is_active: boolean;
}

export interface ReferenceBundle {
  version: number;
  organisms: Organism[];
  antibiotics: Antibiotic[];
  wards: Ward[];
  specimens: Specimen[];
  qa_rules: QARuleConfig[];
}

// --- dashboards ------------------------------------------------------------
export interface VolunteerProgress {
  user_id: string;
  full_name: string;
  today: number;
  this_week: number;
  total: number;
}

export interface TeamDashboard {
  team: Team | null;
  total: number;
  complete: number;
  needs_review: number;
  draft: number;
  void: number;
  open_flags: number;
  open_blocking_flags: number;
  entered_today: number;
  per_day: Record<string, number>;
  volunteers: VolunteerProgress[];
}

// --- exports ---------------------------------------------------------------
export interface ExportFilters {
  team?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  specimen_type?: string | null;
  ward_code?: string | null;
  organism_code?: string | null;
  include_drafts?: boolean;
  include_void?: boolean;
}

export interface ExportRecord {
  id: string;
  user_id: string;
  filters: Record<string, unknown>;
  row_count: number;
  file_hash: string | null;
  status: string;
  error: string | null;
  created_at: string;
}

// --- audit -----------------------------------------------------------------
export interface AuditEntry {
  id: string;
  table_name: string;
  row_id: string | null;
  action: string;
  actor_id: string | null;
  actor_role: string | null;
  actor_name: string | null;
  changes: Record<string, { old: unknown; new: unknown }> | null;
  ip_address: string | null;
  occurred_at: string;
}

export interface SessionRecord {
  id: string;
  user_id: string;
  issued_at: string;
  expires_at: string;
  revoked_at: string | null;
  last_seen_at: string | null;
  ip_address: string | null;
  user_agent: string | null;
}

export interface SystemHealth {
  database_size_bytes: number | null;
  connection_count: number | null;
  isolate_count: number;
  error_rate_24h: number;
  last_backup_at: string | null;
  active_sessions: number;
  sse_subscribers: number;
  environment: string;
  version: string;
}

// --- offline ---------------------------------------------------------------
/** The isolate state machine from docs/09 §9.3. */
export type SyncState =
  | "draft"
  | "pending"
  | "syncing"
  | "synced"
  | "conflict"
  | "error";

/** A row in the local queue. */
export interface LocalIsolate {
  client_uuid: string;
  payload: IsolatePayload;
  state: SyncState;
  /** Assigned by the server once the row lands. */
  record_id: string | null;
  server_id: string | null;
  /** The client's own clock at save, kept for reference (docs/09 §9.11). */
  client_created_at: string;
  updated_at: string;
  /** Why the server rejected it, shown to the volunteer (O-07). */
  error: string | null;
  errorDetail?: unknown;
  attempts: number;
  /** Set when this row is an edit to an already-synced isolate (O-09). */
  editOf?: string | null;
  expectedUpdatedAt?: string | null;
}

export interface ApiError {
  error: string;
  message: string;
  detail?: Record<string, unknown> | null;
  status?: number;
}
